export const prefillAddressAutocomplete = ({ place, questions, optionsMap, setFieldValue }) => {
  const components = place?.address_components || [];
  const get = (type, nameType = 'long_name') => components.find((c) => c.types.includes(type))?.[nameType] || '';

  const streetNumber = get('street_number') || get('intersection');
  const route = get('route') || get('sublocality') || get('sublocality_level_1');
  const address = [streetNumber, route].filter(Boolean).join(' ');
  const city = get('locality');
  const zip = get('postal_code');
  const stateShort = get('administrative_area_level_1', 'short_name');
  const stateLong = get('administrative_area_level_1', 'long_name');

  const findQuestionByKeywords = (keywords) =>
    questions.find((q) =>
      keywords.some((kw) => q.slug?.toLowerCase().includes(kw) || q.name?.toLowerCase().includes(kw)),
    );

  const addressQuestion = findQuestionByKeywords(['street-adress']);
  if (addressQuestion && address) setFieldValue(addressQuestion.slug, address);

  const cityQuestion = findQuestionByKeywords(['city']);
  if (cityQuestion && city) setFieldValue(cityQuestion.slug, city);

  const zipQuestion = findQuestionByKeywords(['zip', 'postal']);
  if (zipQuestion && zip) setFieldValue(zipQuestion.slug, zip);

  const stateQuestion = findQuestionByKeywords(['state']);
  if (stateQuestion && (stateShort || stateLong)) {
    const stateOptions = optionsMap[stateQuestion.id] || [];
    const matched = stateOptions?.find(
      (opt) => opt.slug === stateShort || opt.value === stateShort || opt.name === stateLong,
    );
    if (matched) setFieldValue(stateQuestion.slug, [matched]);
  }
};

export const validateContinueButtonChecks = ({ answer, stepInfo } = {}) => {
  if (stepInfo?.optional) return true;
  if (['string', 'number', 'boolean', 'date', 'textarea'].includes(stepInfo?.type?.slug) && !answer?.answer)
    return false;
  if (['multi-select', 'single-select'].includes(stepInfo?.type?.slug) && !(answer?.selected_option_ids || [])?.length)
    return false;

  if (stepInfo?.type?.slug === 'id-upload') {
    return answer?.answer_attachments?.[0]?.uploaded || answer?.answer_attachments?.[0]?.source_url;
  }
  if (getConditionAction({ stepInfo, answer }) === 'note' && !answer?.note) {
    return false;
  }

  if (getConditionAction({ stepInfo, answer }) === 'not-eligible') {
    return false;
  }

  return true;
};

export const isInEligible = ({ answer, stepInfo } = {}) => getConditionAction({ stepInfo, answer }) === 'not-eligible';

export const getConditionAction = ({ answer: submittedAnswer = {}, stepInfo = {} } = {}) => {
  const { condition_values = '', condition_action = '' } = stepInfo;
  const { answer = '', selected_option_ids = [] } = submittedAnswer;

  if (!condition_values.length) return null;

  const conditionArray =
    typeof condition_values === 'string' ? condition_values.split('|').map((v) => v.trim()) : condition_values;

  const isAnswerMatch = conditionArray?.includes(String(answer));

  const isOptionMatch = selected_option_ids?.some((opt) => conditionArray.includes(String(opt?.name)));

  return isAnswerMatch || isOptionMatch ? condition_action : null;
};

export const getQuestionElementId = (questionId) => `intake-question-${questionId}`;

export const getStepErrorMessage = ({ answer, stepInfo } = {}) => {
  if (validateContinueButtonChecks({ answer, stepInfo })) return '';

  switch (getConditionAction({ answer, stepInfo })) {
    case 'note':
      return 'Please provide the details!';
    case 'not-eligible':
      return '';
    default:
      switch (stepInfo?.type?.slug) {
        case 'id-upload':
          return 'Please upload a photo.';
        case 'boolean':
          return 'Please select Yes or No.';
        case 'single-select':
        case 'multi-select':
          return 'Please select an option.';
        default:
          return 'Please provide an answer.';
      }
  }
};

export const scrollToQuestion = (questionId) =>
  document.getElementById(getQuestionElementId(questionId))?.scrollIntoView({ behavior: 'smooth', block: 'center' });

const INTAKE_SESSION_TOKENS_KEY = 'intake_session_tokens';

const getIntakeSessionTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(INTAKE_SESSION_TOKENS_KEY)) || {};
  } catch {
    return {};
  }
};

export const getIntakeSessionToken = (intakeId) => (intakeId ? getIntakeSessionTokens()?.[intakeId] : undefined);

export const setIntakeSessionToken = (intakeId, sessionToken) => {
  if (!intakeId || !sessionToken) return;
  try {
    localStorage.setItem(
      INTAKE_SESSION_TOKENS_KEY,
      JSON.stringify({ ...getIntakeSessionTokens(), [intakeId]: sessionToken }),
    );
  } catch {
    return;
  }
};

const convertAnswers = (data) =>
  Object.values(data).reduce((acc, questions) => {
    Object.values(questions).forEach(({ slug, answer }) => {
      if (slug && answer !== undefined) {
        acc[slug] = answer;
      }
    });

    return acc;
  }, {});

const verifyOperatorRule = ({ rule = {}, answerObj = {}, intakeData = {} }) => {
  const evaluatedAgainst = rule?.evaluated_against;
  const evaluatedFrom = (rule?.evaluation_type === 'response' ? intakeData : answerObj)?.[rule?.evaluated_from];

  switch (rule?.operator) {
    case 'eq':
      return evaluatedFrom === evaluatedAgainst;
    case 'in':
      return (evaluatedFrom || []).includes(evaluatedAgainst);
    default:
      return true;
  }
};

export const filterIntakeSectionQuestions = ({ currentSection = {}, answers = {}, intakeData = {} }) => {
  const answerObj = convertAnswers(answers);
  const questions = currentSection?.intake_questions
    ?.map((question) => {
      const questionActions = question?.settings?.actions || [];
      if (questionActions?.length) {
        let updatedQuestionObj = { ...question };

        questionActions.map((action) => {
          switch (action.type) {
            case 'visibility':
              switch (action.logical_operator) {
                case 'AND':
                  if (
                    !(action.rules || []).every((rule) => {
                      return verifyOperatorRule({ rule, answerObj, intakeData });
                    })
                  ) {
                    updatedQuestionObj.hide = true;
                  }
                  return {};
                case 'OR':
                  if (
                    !(action.rules || []).some((rule) => {
                      return verifyOperatorRule({ rule, answerObj, intakeData });
                    })
                  ) {
                    updatedQuestionObj.hide = true;
                  }
                  return {};
                default:
                  return {};
              }
            default:
              return {};
          }
        });

        return updatedQuestionObj;
      } else {
        return question;
      }
    })
    .filter((q) => !q?.hide);

  return { ...currentSection, intake_questions: questions };
};

export const getRetryAfterSeconds = (responseData) => {
  const retryAfter = Number(responseData?.retry_after ?? responseData?.data?.retry_after);
  return retryAfter > 0 ? retryAfter : 0;
};

export const formIntakePath = ({ stepInfo, intakeData, answers }) => {
  const answerObj = convertAnswers(answers);
  const matches = [...(stepInfo?.options_api || '').matchAll(/\{\{(.*?)\}\}/g)].map((match) => match[1]);

  const pathValues = {
    ':voucher_token': intakeData?.token,
    ...(matches?.[0] ? { [`{{${matches?.[0]}}}`]: answerObj?.[matches?.[0]] } : {}),
  };

  return Object.entries(pathValues).reduce(
    (path, [placeholder, value]) => path.replaceAll(placeholder, value ?? ''),
    stepInfo?.options_api || '',
  );
};
